import React, { useEffect, useState } from "react";
import { signOut } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


interface RSVPResponse {
  id: string;
  index: number;
  name: string;
  email: string;
  attending: string;
  ageRange: number;
  foodRest: string;
  menu: string;
  phone: number;
}

const AdminDashboard: React.FC = () => {
  const [responses, setResponses] = useState<RSVPResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: keyof RSVPResponse; direction: "asc" | "desc" } | null>(null);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/admin";
  };
  
  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(responses.map((res) => ({
      Name: res.name,
      Email: res.email,
      Attending: res.attending,
      AgeRange: res.ageRange,
      FoodRestriction: res.foodRest,
      Menu: res.menu,
      Phone: res.phone
    })));

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RSVPs");

    // Convert workbook to binary and save
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, `rsvp-responses-${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "rsvp"));
        const data = querySnapshot.docs.map((doc, index) => ({
          id: doc.id,
          index: index + 1, // Add row index
          ...doc.data(),
        })) as RSVPResponse[];
        setResponses(data);
      } catch (error) {
        console.error("Error fetching RSVP responses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, []);

  const handleSort = (key: keyof RSVPResponse) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedData = [...responses].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setResponses(sortedData);
  };

  if (loading) {
    return <p>Loading RSVP responses...</p>;
  }

  return (
    <div>
      <h2>Admin Dashboard</h2>
      <p>Welcome to the admin panel!</p>
      <button onClick={handleLogout}>Logout</button>
      <button onClick={handleExport}>Export</button>

      <div className="rsvp-table-container">
      <h2>RSVP Responses</h2>
      <table className="rsvp-table">
        <thead>
          <tr>
            <th>#</th> {/* Index column */}
            <th onClick={() => handleSort("name")}>Name ⬍</th>
            <th onClick={() => handleSort("email")}>Email ⬍</th>
            <th onClick={() => handleSort("attending")}>Attending ⬍</th>
            <th onClick={() => handleSort("ageRange")}>Age Range ⬍</th>
            <th onClick={() => handleSort("foodRest")}>Food Restrictions ⬍</th>
            <th onClick={() => handleSort("menu")}>Menu ⬍</th>
            <th onClick={() => handleSort("phone")}>Phone ⬍</th>
          </tr>
        </thead>
        <tbody>
        {responses.length > 0 ? (
            responses.map((response, index) => (
              <tr key={response.id}>
                <td>{index + 1}</td> {/* Display row number */}
                <td>{response.name || "N/A"}</td>
                <td>{response.email || "N/A"}</td>
                <td>{response.attending || "N/A"}</td>
                <td>{response.ageRange || "N/A"}</td>
                <td>{response.foodRest || "N/A"}</td>
                <td>{response.menu || "N/A"}</td>
                <td>{response.phone || "N/A"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5}>No responses yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </div>
  );
};

export default AdminDashboard;
